import { _isNil, _isNotBlank, _notNil, _toNumber, uuid } from '@/littledash';
import { datatableFormulaParser } from '@/utils/FormulaDsl';
import { StringUtils } from '@/utils/String.utils';
import { parse as parseDuration, serialize as serializeDuration } from 'tinyduration';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import type { Nullable } from '@/model/Common.model';
import type {
  ChildColumn,
  DataTableCellIdCoordinate,
  DataTableCellReference,
  DataTableColumn,
  DataTableColumnApiId,
  DataTableColumnType,
  DataTableFormula,
  DataTableMetadataColumn,
  DataTableMetadataFieldType,
  DataTableRow,
  DataTableRowApiId,
  DataTableService,
  FillableColumn,
} from '@/components/UI/DataTableV2/DataTable.model';
import { isApiId } from '@/utils/ApiId.utils';
import { selectFieldTypeConfig } from '@/components/UI/DataTableV2/DataTable.model';

/**
 * Type guard to check if a column is fillable.
 * @param column - The column to check.
 * @returns true if the column is fillable, false otherwise.
 */
export const isFillableColumn = (column: DataTableColumn | undefined): column is FillableColumn =>
  _notNil(column) &&
  (column.type === 'timestampBaseline' || column.type === 'sampleCollectedAt' || column.type === 'sampleDoseTime');

export const toCellRef = (coordinate?: DataTableCellIdCoordinate): DataTableCellReference =>
  _notNil(coordinate) ? `[${coordinate.column},${coordinate.row}]` : ('[,]' as unknown as DataTableCellReference);

export const cellReferencePattern = /^\[(?<column>dtc_[a-zA-Z0-9]+),(?<row>dtr_[a-zA-Z0-9]+)]$/;
export const fromCellRef = (ref?: DataTableCellReference): DataTableCellIdCoordinate | undefined => {
  const { column, row } = cellReferencePattern.exec(ref ?? '')?.groups ?? { column: undefined, row: undefined };
  if (_notNil(column) && _notNil(row)) {
    return { column: column as DataTableColumnApiId, row: row as DataTableRowApiId };
  }
};

export const randomDataTableColumnId = (): DataTableColumnApiId => `dtc_${uuid().replaceAll('-', '')}`;
export const randomDataTableRowId = (): DataTableRowApiId => `dtr_${uuid().replaceAll('-', '')}`;
export const randomDataTableCellIdCoordinate = (): DataTableCellIdCoordinate => ({
  column: randomDataTableColumnId(),
  row: randomDataTableRowId(),
});
export const dataTableColumnGenerator = (count: number, type: DataTableColumnType = 'number'): Array<DataTableColumn> =>
  Array.from({ length: count }).map((_, index) => ({
    id: randomDataTableColumnId(),
    type,
    name: `column ${index + 1}`,
    locked: false,
    read_only: false,
    displayed: true,
  })) as Array<DataTableColumn>;
export const dataTableRowGenerator = (count: number, withGroup = false): Array<DataTableRow> =>
  Array.from({ length: count }).map((_, index) => ({
    id: randomDataTableRowId(),
    type: 'animal',
    animal: {
      id: index + 1,
      name: `Animal ${index + 1}`,
      dob: '',
      study_group: withGroup
        ? {
            id: `grp_${uuid().replaceAll('-', '')}`,
            name: `Group ${index + 1}`,
            no: index + 1,
            color: StringUtils.randomHexColor(),
            max_subjects: 10,
            created_at: '2022-01-01T00:00:00.000000Z',
            updated_at: '2022-01-01T00:00:00.000000Z',
          }
        : undefined,
      created_at: '',
      sex: 'm',
      metadata: [],
    },
  })) as unknown as Array<DataTableRow>;

const greedyVariableReplacePattern = /[^a-zA-Z0-9_]+/g;
export const columnNameToFormulaVariable = (name: string, prefix = ''): string => {
  const variable = name
    .toLowerCase()
    .replaceAll(greedyVariableReplacePattern, ' ')
    .trim()
    .replaceAll(greedyVariableReplacePattern, '_');
  return `${prefix}${variable}`;
};

export const generateColumnFormulaName = (column: DataTableColumn) => {
  const referenceDate =
    column.type === 'measurement' || column.type === 'observation' ? column.reference_date : undefined;
  return `${column.name ?? ''}${_notNil(referenceDate) ? '_' + referenceDate : ''}`;
};

export const substituteFormulaVariables = async <From extends string, To extends string>(
  formula: string,
  variables: Record<From, To>,
  options: { ignoreInvalidVariables?: boolean } = {}
) =>
  new Promise<{ formula: string; placeholders: Record<To, From> }>((resolve, reject) => {
    const cursor = datatableFormulaParser.parse(formula).cursor();
    const formulaComponents: Array<string> = [];
    const formulaPlaceholders = {} as Record<To, From>;
    do {
      if (cursor.node.type.isError) {
        reject(new Error(`invalid syntax @ [${formula.slice(cursor.from, cursor.to)}]`));
        break;
      }
      if (_isNil(cursor.node.firstChild)) {
        const component = formula.slice(cursor.from, cursor.to);
        if (cursor.node.name === 'Variable') {
          const variable = component.substring(1) as From;
          const placeholder = variables[variable];
          if (_notNil(placeholder)) {
            formulaComponents.push(`$${placeholder}`);
            formulaPlaceholders[placeholder] = component as From;
          } else if (options?.ignoreInvalidVariables ?? false) {
            formulaComponents.push('#REF!');
          } else {
            reject(new Error('invalid variable ' + component));
            break;
          }
        } else {
          formulaComponents.push(component);
        }
      }
    } while (cursor.next());
    resolve({
      formula: formulaComponents.join(' '),
      placeholders: formulaPlaceholders,
    });
  });
export const formulaVariablesToColumnIds = async (
  formula: string,
  leafColumns: Array<DataTableColumn>
): Promise<DataTableFormula> => {
  const variables = leafColumns.reduce<Record<string, DataTableColumnApiId>>((acc, column) => {
    acc[columnNameToFormulaVariable(generateColumnFormulaName(column))] = column.id;
    return acc;
  }, {});
  const { formula: value, placeholders } = await substituteFormulaVariables(formula, variables);
  return {
    value,
    placeholders: (Object.keys(placeholders) as Array<DataTableColumnApiId>).reduce<DataTableFormula['placeholders']>(
      (acc, columnId) => {
        acc[columnId] = { type: 'column', value: columnId };
        return acc;
      },
      {}
    ),
  };
};
export const formulaVariablesFromColumnIds = async (
  formula: string,
  leafColumns: Readonly<Array<DataTableColumn>>,
  options: { ignoreInvalidVariables?: boolean } = {}
): Promise<string> => {
  const variables = leafColumns.reduce<Record<DataTableColumnApiId, string>>((acc, column) => {
    acc[column.id] = columnNameToFormulaVariable(generateColumnFormulaName(column));
    return acc;
  }, {});
  try {
    const { formula: value } = await substituteFormulaVariables(formula, variables, {
      ignoreInvalidVariables: options?.ignoreInvalidVariables,
    });
    return value;
  } catch (err) {
    throw new InVivoError('Could not get variables from column ids', {
      cause: err,
      slug: 'datatable-formula-variable-conversion',
    });
  }
};

interface ChunkOptions {
  chunkSize?: number;
  maxChunks?: number;
}

export const chunk = <T>(array: Array<T>, options?: ChunkOptions): Array<Array<T>> => {
  const actualChunkSize = Math.max(
    Math.min(options?.chunkSize ?? 1000, array.length),
    Math.ceil(array.length / (options?.maxChunks ?? array.length))
  );
  return Array.from({ length: Math.ceil(array.length / actualChunkSize) }, (v, idx) =>
    array.slice(idx * actualChunkSize, idx * actualChunkSize + actualChunkSize)
  );
};

export const makeCellData = (dataTableService: DataTableService) => {
  const result: Array<{ row_id: DataTableRowApiId; column_id: DataTableColumnApiId; value: string }> = [];
  dataTableService.rows.forEach((row) => {
    dataTableService.leafColumns.forEach((column) => {
      result.push({
        column_id: column.id,
        row_id: row.id,
        value: (Math.random() * 100).toPrecision(3),
      });
    });
  });
  return result;
};

export const getValueAndUnitFromColumn = (column: DataTableColumn): Record<string, string> => {
  try {
    const parsedDuration = parseDuration(column.name ?? '');
    const isNegative = parsedDuration.negative ?? false;
    const validUnit = Object.entries(parsedDuration as Record<string, string>).reduce<Record<string, string>>(
      (acc, [key, value]) => {
        if (_notNil(value) && Number(value) !== 0 && typeof key === 'string') {
          acc[key] = `${isNegative ? '-' : ''}${value}`;
        }
        return acc;
      },
      {}
    );
    const unit = Object.keys(validUnit).at(0) ?? '';
    const amount = validUnit[unit] ?? '';
    return { unit, amount };
  } catch (err) {
    ExceptionHandler.captureException(
      new InVivoError('Failed to parse time unit from datatable column', {
        cause: err,
        slug: 'datatable-timepoint-parse-error',
      })
    );
    return { unit: '', amount: '' };
  }
};

export const serializeTimepointValue = (value: string, unit: string) => {
  const isNegative = Number(value) < 0;
  const number = Math.abs(Number(value));
  const unitKey = unit === 'H' ? 'hours' : unit === 'M' ? 'minutes' : 'seconds';
  return serializeDuration({ [unitKey]: number, negative: isNegative });
};

/**
 * Type guard to check if a column has the 'displayed' property.
 * Used for filtering leaf columns based on display state.
 *
 * @param column - The column to check
 * @returns true if column has displayed property
 */
export const hasDisplayedProperty = (column: DataTableColumn): column is DataTableColumn & { displayed: boolean } => {
  return 'displayed' in column && typeof column.displayed === 'boolean';
};

/**
 * Type guard to check if a column carries the 'expanded' property.
 *
 * Only sample columns do; a dosingSession parent has no such field, so its group's
 * expansion state has to be derived from child visibility instead — see
 * {@link isColumnGroupExpanded}.
 *
 * @param column - The column to check
 * @returns true if column has expanded property
 */
export const hasExpandedProperty = (column: DataTableColumn): column is DataTableColumn & { expanded: boolean } => {
  return 'expanded' in column && typeof column.expanded === 'boolean';
};

/**
 * Checks if a column is in expanded state.
 *
 * Column-level only: a parent without an 'expanded' field always reads as expanded, which
 * is wrong for a collapsed group. Use {@link isColumnGroupExpanded} for group headers.
 *
 * @param column - The column to check
 * @returns true if column is expanded, false if explicitly collapsed
 */
export const isColumnExpanded = (column: DataTableColumn): boolean => {
  return 'expanded' in column ? column.expanded !== false : true;
};

/**
 * Determines whether a column group is expanded.
 *
 * Prefers the parent's own 'expanded' flag when it has one (sample columns), and otherwise
 * derives the state from child visibility: a group is expanded only while every child is
 * still displayed. Without the fallback a dosingSession group — whose schema has no
 * 'expanded' field — would report as permanently expanded and its toggle could only ever
 * collapse, never restore.
 *
 * @param parentColumn - The group's parent column
 * @param childColumns - Every child of the parent, displayed or not
 * @param visibleChildColumns - The subset of children currently displayed
 * @returns true if the group is expanded
 */
export const isColumnGroupExpanded = (
  parentColumn: DataTableColumn,
  childColumns: ReadonlyArray<DataTableColumn>,
  visibleChildColumns: ReadonlyArray<DataTableColumn>
): boolean => {
  if (hasExpandedProperty(parentColumn)) {
    return parentColumn.expanded;
  }
  return visibleChildColumns.length === childColumns.length;
};

/**
 * Type guard to check if a column is a child column (has a parent column).
 *
 * Child columns are columns that belong to a parent column group. In the Benchling InVivo
 * DataTable, sample parent columns can be expanded to show child columns (e.g., "Sample Status",
 * "Sample ID", "Collected At"). This type guard identifies those child columns by checking for
 * a valid parent_api_id field.
 *
 * **Type Safety:** The return type narrows the DataTableColumn discriminated union to only column
 * types that have a parent_api_id field, with that field guaranteed to be a valid DataTableColumnApiId
 * (not null or undefined).
 *
 * **Validation:** Uses isApiId('dtc', ...) to ensure parent_api_id follows the correct format
 * (prefix 'dtc' for DataTable Column). This prevents false positives from malformed data.
 *
 * @param column - The column to check for parent_api_id
 * @returns true if the column has a valid parent_api_id property, narrowing the type to ChildColumn
 *
 * @example
 * ```typescript
 * const childColumns = columns.filter(isChildColumn);
 * childColumns.forEach(child => {
 *   const parentId: DataTableColumnApiId = child.parent_api_id;
 * });
 * ```
 */
export const isChildColumn = (column: DataTableColumn): column is ChildColumn => {
  return 'parent_api_id' in column && isApiId('dtc', column.parent_api_id);
};

/**
 * Check if field type is a select-based field (single or multi)
 */
export const isSelectFieldType = (fieldType?: string): fieldType is DataTableMetadataFieldType => {
  return _notNil(fieldType) && fieldType in selectFieldTypeConfig;
};

/**
 * Check if field type indicates multi-select
 */
export const isMultiSelectFieldType = (fieldType?: string): boolean => {
  return (
    _notNil(fieldType) &&
    fieldType in selectFieldTypeConfig &&
    selectFieldTypeConfig[fieldType as DataTableMetadataFieldType].supportsMultiple
  );
};

/**
 * Type guard to check if a column is a metadata column with select or multi-select field type.
 *
 * This is useful for identifying columns that store values as JSON-encoded arrays and need
 * special rendering logic (e.g., chip display for readonly cells).
 *
 * @param column - The column to check, or undefined
 * @returns true if the column is a metadata column with select/multi-select field type,
 *          narrowing the type to DataTableMetadataColumn
 *
 * @example
 * ```typescript
 * const column = dataTableService.columnById(columnId);
 * if (isMetadataSelectColumn(column)) {
 *   const glossaryApiId = column.metadata.metadata_glossary_api_id;
 *   const fieldType = column.metadata.field_type; // 'select' | 'multi_select'
 * }
 * ```
 */
export const isMetadataSelectColumn = (column: DataTableColumn | undefined): column is DataTableMetadataColumn => {
  return _notNil(column) && column.type === 'metadata' && isSelectFieldType(column.metadata?.field_type);
};

export const isDateFieldType = (fieldType?: string): boolean => {
  return fieldType === 'date';
};

/**
 * Parse stored JSON value to array of strings.
 *
 * Handles various input formats:
 * - Array of strings/numbers/booleans (converts all to trimmed strings)
 * - Single string/number/boolean value (returns as single-element string array)
 * - Null/undefined/empty strings (returns empty array)
 * - Invalid JSON starting with [, ", or { (returns raw value as single-element array, reports to Sentry at warning level)
 * - Other non-JSON text (returns raw value as single-element array, no Sentry report)
 * - Valid JSON objects (returns empty array, as objects are not valid select values)
 *
 * @param value - String value (typically JSON-encoded), null, or undefined
 * @param glossaryApiId - Optional metadata glossary API ID for Sentry error context
 * @returns Array of parsed string values, empty array if null/empty, or raw value as single-element array if unparseable
 */
export const parseSelectValue = (value: Nullable<string>, glossaryApiId?: string): Array<string> => {
  if (_isNotBlank(value)) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.reduce<Array<string>>((acc, item) => {
          if (typeof item === 'string' && _isNotBlank(item)) {
            acc.push(item.trim());
          } else if (typeof item === 'number' || typeof item === 'boolean') {
            acc.push(String(item));
          }
          return acc;
        }, []);
      }
      if (typeof parsed === 'string' && _isNotBlank(parsed)) {
        return [parsed.trim()];
      }
      if (typeof parsed === 'number' || typeof parsed === 'boolean') {
        return [String(parsed)];
      }
    } catch (error) {
      const trimmedValue = value.trimStart();
      if (trimmedValue.startsWith('[') || trimmedValue.startsWith('"') || trimmedValue.startsWith('{')) {
        ExceptionHandler.captureException(
          new InVivoError('Failed to parse select cell value', {
            cause: error,
            slug: 'datatable-select-cell-parse',
            level: 'warning',
            context: _isNotBlank(glossaryApiId) ? { metadataGlossaryApiId: glossaryApiId } : undefined,
          })
        );
      }
      return [value];
    }
  }
  return [];
};

export const parsePaginationFromUrl = (
  nextUrl: string | null | undefined,
  fallbacks: { page: number; perPage: number }
): { page: number; perPage: number } => {
  if (_isNil(nextUrl)) {
    return fallbacks;
  }

  const url = new URL(nextUrl);
  const pageParam = url.searchParams.get('page');
  const perPageParam = url.searchParams.get('perPage');

  return {
    page: _toNumber(pageParam, fallbacks.page),
    perPage: _toNumber(perPageParam, fallbacks.perPage),
  };
};
